import type { FaqItem } from '../components/Faq';
import type { IconName } from '../components/icons.generated';

// NOTE: option labels and trust copy come from the Stitch design ("Studio Consultation").
// The FAQ answers were rewritten to avoid unverified figures; edit freely.

export type TrustItem = { icon: IconName; title: string; text: string };

export const trustItems: TrustItem[] = [
  { icon: 'verified', title: '100% Craft Guarantee', text: 'German joinery hardware & lifetime marine-ply warranty' },
  { icon: 'lock', title: 'Strict Confidentiality', text: 'Non-disclosure execution for private residences & estates' },
  { icon: 'architecture', title: 'Zero-Obligation Scoping', text: 'Complimentary spatial assessment & preliminary feasibility' },
];

export type Typology = { value: string; icon: IconName; title: string; text: string; wide?: boolean; badge?: string };

export const typologies: Typology[] = [
  { value: 'Residential Villa', icon: 'villa', title: 'Residential Villa', text: 'Independent residences, ancestral estates & farm retreats' },
  { value: 'Penthouse Suite', icon: 'apartment', title: 'Penthouse Suite', text: 'High-rise luxury flats, duplexes & city sanctuaries' },
  { value: 'Commercial / Corporate Office', icon: 'domain', title: 'Corporate Office', text: 'Executive boardrooms, headquarters & creative studios' },
  { value: 'Luxury Hospitality & Dining', icon: 'hotel', title: 'Luxury Hospitality', text: 'Boutique hotels, culinary pavilions & wellness spas' },
  {
    value: 'Bespoke Kitchen & Joinery',
    icon: 'countertops',
    title: 'Haute Modular Kitchen & Millwork',
    text: 'Comprehensive kitchen transformations, wardrobes & bespoke architectural joinery',
    wide: true,
    badge: 'Specialist Wing',
  },
];

export const AREA = { min: 500, max: 15000, step: 250, initial: 3200 };

export const areaPresets: Array<{ value: number; label: string }> = [
  { value: 1200, label: '1,200 sq.ft (Apartment)' },
  { value: 3200, label: '3,200 sq.ft (Villa)' },
  { value: 6500, label: '6,500 sq.ft (Mansion)' },
  { value: 12000, label: '12,000+ sq.ft (Commercial)' },
];

/** Description shown under the square-footage gauge (also sent as the "scale" field). */
export function areaTier(sqft: number): string {
  if (sqft < 1500) return 'Compact Urban Suite, Boutique Apartment, or Atelier Showcase';
  if (sqft < 4500) return 'Comprehensive 3 to 4 Bedroom Luxury Villa or Estate Wing';
  if (sqft < 8000) return 'Expansive High-Profile Residence, Commercial Headquarters, or Duplex';
  return 'Grand Heritage Manor, Resort Property, or Multi-Storey Commercial Unit';
}

export type Choice = { value: string; title: string; text: string; default?: boolean };

export const timelines: Choice[] = [
  { value: 'Immediate (1 - 3 Months)', title: '1 - 3 Months', text: 'Rapid Execution' },
  { value: 'Medium Term (3 - 6 Months)', title: '3 - 6 Months', text: 'Standard Atelier Pace', default: true },
  { value: 'Visionary (6 - 12+ Months)', title: '6 - 12+ Months', text: 'Architectural Villa Build' },
];

export const budgets: Choice[] = [
  { value: '₹15 Lakhs - ₹30 Lakhs', title: '₹15L – ₹30L', text: 'Modular Kitchen + Select Joinery Suites' },
  { value: '₹30 Lakhs - ₹60 Lakhs', title: '₹30L – ₹60L', text: 'Complete 3/4 BHK Premium Turnkey Execution', default: true },
  { value: '₹60 Lakhs - ₹1.2 Crores', title: '₹60L – ₹1.2 Cr', text: 'Haute Bespoke Villa with Italian Finishes' },
  { value: '₹1.2 Crores+ (Atelier Reserve)', title: '₹1.2 Cr+ Atelier Reserve', text: 'Full Architectural Transformation & Heritage Estates' },
];

/** Labels for the progress indicator, one per form step. */
export const stepLabels = ['Space Typology', 'Spatial Scale', 'Target Timeline & Budget', 'Client Coordinates & Brief'];

export const regionTags = ['Nagercoil', 'Trivandrum', 'Kanyakumari', 'Tirunelveli', 'Madurai / Kochi'];

export const materialStats = [
  { value: '450+', label: 'Archived Physical Samples' },
  { value: '14 Days', label: 'Preliminary 3D Spatial Walkthrough' },
];

export const contactPrincipal = {
  name: 'Ar. J. Rajan',
  role: 'Principal Spatial Architect',
  quote:
    'Interior space is neither an ornament nor a commodity. It is an enduring tactile vessel for memories, human posture, and restorative silence. We welcome your project brief.',
};

export const contactFaqs: FaqItem[] = [
  {
    question: 'How does the consultation and turnkey process work?',
    answer:
      'Every project moves through four stages. Phase I, spatial discovery and feasibility: we visit the site, take measurements, understand how you live or work, and agree a preliminary budget framework. Phase II, 3D design and material moodboard: you review 3D visualisations and sign off physical finish samples at our Nagercoil studio. Phase III, joinery fabrication: cabinetry, wardrobes and panelling are built in the workshop while the site is prepared. Phase IV, installation and handover: our site team installs, works through a snag list, cleans and hands the space over to you.',
  },
  {
    question: 'Do you undertake out-of-station projects beyond Nagercoil & Trivandrum?',
    answer:
      'Yes. Our studio and workshop are in Nagercoil, and we take on projects across Kanyakumari district, Trivandrum, Tirunelveli, Madurai and Kochi. For projects outside Nagercoil we plan site visits, deliveries and installation in advance, and we talk through the scope and logistics for your location in the first consultation.',
  },
  {
    question: 'What is the typical timeline for a complete villa interior?',
    answer:
      'It depends on the size of the villa, whether the scope is full turnkey or selected rooms, the finishes you choose and how quickly design decisions are signed off. The schedule is fixed once the 3D design is frozen. Because most cabinetry and millwork is built in our workshop rather than on site, the on-site phase is shorter than the overall programme. After the first site visit we share a written schedule with clear milestones for your project.',
  },
  {
    question: 'How are material warranties and post-handover support handled?',
    answer:
      'Warranty terms depend on the boards, finishes and hardware specified for your project, as each carries its own manufacturer cover, and they are set out in writing in your quotation before work begins. After handover our team stays reachable by phone and WhatsApp for follow-up care such as hinge adjustments, seal checks and touch-ups.',
  },
];
